import React, { useEffect, useState } from 'react';
import { generatePath, Link, Route, Switch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, getContrastRatio, Typography } from '@mui/material';
import { CheckBox as CheckBoxIcon } from '@mui/icons-material';
import { Colors } from '../../../assets/theme/Colors';
import routes from '../../../routes';
import VideoModule from '../Components/VideoModule';
import NeedToKnowModule from '../Components/NeedToKnowModule';
import QuizModule from '../Components/QuizModule';
import useActiveUser from '../../../hooks/useActiveUser';
import { setCoursesState } from '../../../actions/ui';
import {
  NEED_TO_KNOW_MODULE_NAME,
  QUIZ_MODULE_NAME,
  VIDEO_MODULE_NAME,
} from '../LearningCenterMobile/LearningCenterMobileView';
import OverlayLoader from '../../../components/OverlayLoader';
import { fetchUserCourses } from '../../../actions/users';

const linkContainerSx = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: 0,
};
const checkboxSx = {
  fontSize: '18px',
  color: Colors.green.light,
};
const subLinkSx = (theme) => ({
  textDecoration: 'none',
  color: Colors.gray,
  marginLeft: '10px',
  padding: '3px 8px',
  position: 'relative',
  '&.active': {
    color: 'primary.dark',
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: 5,
      backgroundColor: 'primary.main',
      filter:
        getContrastRatio(theme.palette.primary.main, '#fff') > 4.5
          ? 'brightness(4) saturate(0.1)'
          : 'brightness(1.5) saturate(0.7)',
      zIndex: -1,
    },
  },
});

const videoTab = 'video';
const needToKnowTab = 'need-to-know';
const quizTab = 'quiz';

const LearningCenterDesktopContainer = () => {
  const activeUser = useActiveUser();
  const dispatch = useDispatch();
  const coursesEntities = useSelector((state) => state.entities.users.courses);
  const courses = coursesEntities.byUserId[activeUser.id] || [];
  const [currentCourse, setCurrentCourse] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [activeSubTab, setActiveSubTab] = useState(videoTab);
  const coursesState = useSelector((state) => state.ui.courses);
  const currentUserCoursesState = coursesState[activeUser.id];

  useEffect(() => {
    if (activeUser.id) {
      dispatch(fetchUserCourses(activeUser.id));
    }
  }, [activeUser.id]);

  useEffect(() => {
    if (currentUserCoursesState) {
      setActiveTab(currentUserCoursesState?.activeCourse);
    } else if (courses.length > 0) {
      setActiveTab(courses[0]?.id);
    }
  }, [currentUserCoursesState]);

  useEffect(() => {
    if (currentUserCoursesState?.activeCourse && courses.length > 0) {
      const course = courses.find((c) => c.id === currentUserCoursesState.activeCourse);
      if (course) {
        setCurrentCourse(course);
      }
    } else if (courses.length > 0) {
      setCurrentCourse(courses[0]);
    }
  }, [courses]);

  const setCurrentCoursesState = (courseId) => dispatch(setCoursesState(activeUser.id, courseId));

  const paths = [
    generatePath(routes.learn),
    generatePath(routes.learn, { tab: videoTab }),
    generatePath(routes.learn, { tab: needToKnowTab }),
    generatePath(routes.learn, { tab: quizTab }),
  ];

  if (!currentCourse?.id) {
    return <OverlayLoader isLoading />;
  }

  return (
    <OverlayLoader isLoading={coursesEntities.inflight}>
      <Typography variant="h5">Learn About Food Rescue</Typography>
      <Box mt={5} display="flex" gap="16px">
        <Box minWidth="max-content">
          {courses.map((course) => {
            const videoModule = (course.modules || []).find((module) => module.name === VIDEO_MODULE_NAME);
            const needToKnowModule = (course.modules || []).find((module) => module.name === NEED_TO_KNOW_MODULE_NAME);
            const quizModule = (course.modules || []).find((module) => module.name === QUIZ_MODULE_NAME);
            const allModulesFinished = (course?.modules || []).every((module) => module.completed);
            return (
              <Box key={course.id}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center' }}
                  onClick={() => {
                    setActiveSubTab(videoTab);
                    setActiveTab(course.id);
                    setCurrentCourse(course);
                  }}
                >
                  <Box
                    component={Link}
                    sx={{
                      textDecoration: 'none',
                      color: Colors.gray,
                      paddingLeft: '10px',
                      marginRight: 1,
                      '&.active': {
                        color: 'primary.main',
                        position: 'relative',
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          left: -3,
                          top: 0,
                          height: '100%',
                          borderLeft: '4px solid',
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                    className={course.id !== activeTab ? '' : 'active'}
                    to={generatePath(routes.learn)}
                  >
                    {course.title}
                  </Box>

                  {allModulesFinished && <CheckBoxIcon sx={checkboxSx} />}
                </Typography>

                <Box
                  sx={{
                    overflow: 'hidden',
                    maxHeight: 0,
                    opacity: 0,
                    transition: 'all 0.5s ease-in-out',
                    '&.show': {
                      maxHeight: '200px',
                      opacity: 1,
                      transition: 'max-height 1s ease-in-out, opacity 0.7s ease-in-out',
                    },
                  }}
                  className={activeTab === course.id ? 'show' : ''}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={linkContainerSx}
                    onClick={() => {
                      setCurrentCourse(course);
                      setActiveSubTab(videoTab);
                    }}
                  >
                    <Box
                      component={Link}
                      sx={subLinkSx}
                      className={activeSubTab !== videoTab ? '' : 'active'}
                      to={generatePath(routes.learn, { tab: videoTab, id: course.id })}
                    >
                      Video {course.videoDuration ? `- ${course.videoDuration} min` : ''}
                    </Box>

                    {videoModule?.completed && <CheckBoxIcon sx={checkboxSx} />}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={linkContainerSx}
                    onClick={() => {
                      setCurrentCourse(course);
                      setActiveSubTab(needToKnowTab);
                    }}
                  >
                    <Box
                      component={Link}
                      sx={subLinkSx}
                      className={activeSubTab !== needToKnowTab ? '' : 'active'}
                      to={generatePath(routes.learn, { tab: needToKnowTab, id: course.id })}
                    >
                      Need To Know {course.needToKnowDuration ? `- ${course.needToKnowDuration} min` : ''}
                    </Box>

                    {needToKnowModule?.completed && <CheckBoxIcon sx={checkboxSx} />}
                  </Typography>

                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={linkContainerSx}
                    onClick={() => {
                      setCurrentCourse(course);
                      setActiveSubTab(quizTab);
                    }}
                  >
                    <Box
                      component={Link}
                      sx={subLinkSx}
                      className={activeSubTab !== quizTab ? '' : 'active'}
                      to={generatePath(routes.learn, { tab: quizTab, id: course.id })}
                    >
                      Quiz {course.quizDuration ? `- ${course.quizDuration} min` : ''}
                    </Box>

                    {quizModule?.completed && <CheckBoxIcon sx={checkboxSx} />}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ width: 'calc(100% - 170px)' }}>
          <Switch>
            <Route path={paths[0]} exact>
              <VideoModule
                userId={activeUser.id}
                course={currentCourse}
                setCurrentCoursesState={setCurrentCoursesState}
              />
            </Route>
            <Route path={paths[1]}>
              <VideoModule
                userId={activeUser.id}
                course={currentCourse}
                setCurrentCoursesState={setCurrentCoursesState}
              />
            </Route>
            <Route path={paths[2]}>
              <NeedToKnowModule
                userId={activeUser.id}
                course={currentCourse}
                setCurrentCoursesState={setCurrentCoursesState}
              />
            </Route>
            <Route path={paths[3]}>
              <QuizModule
                userId={activeUser.id}
                course={currentCourse}
                user={activeUser}
                setCurrentCoursesState={setCurrentCoursesState}
              />
            </Route>
          </Switch>
        </Box>
      </Box>
    </OverlayLoader>
  );
};

export default LearningCenterDesktopContainer;
